/**
 * BehavioralPanel — social/behavioral signal for a security.
 *
 * Phase 1 / G25. Displays:
 *  • Aggregate sentiment score (net score, bullish/bearish/neutral breakdown)
 *  • Recent social posts feed (most engaging, linked to source)
 *
 * Handles "no data yet" gracefully — shows an empty state with a one-click
 * "Refresh social data" trigger that calls POST /social/{id}/ingest.
 */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, RefreshCw, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { getSentiment, ingestSocial, listSocialPosts } from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { EmptyState, LoadingState } from "@/components/ui/States";
import { Button } from "@/components/ui/Button";
import type { SentimentScoreOut, SocialPostOut } from "@/lib/api/types";
import { clsx } from "clsx";

type Props = {
  securityId: number;
};

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function fmtDate(iso: string): string {
  return iso.substring(0, 10);
}

function fmtDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso.substring(0, 16).replace("T", " ");
  }
}

function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen).trimEnd() + "…";
}

const PLATFORM_LABEL: Record<string, string> = {
  stocktwits: "StockTwits",
  reddit: "Reddit",
  hn: "Hacker News",
  aggregate: "Aggregate",
  llm_aggregate: "LLM",
};

function platformLabel(p: string): string {
  return PLATFORM_LABEL[p.toLowerCase()] ?? p;
}

// ---------------------------------------------------------------------------
// Sentiment score widget
// ---------------------------------------------------------------------------

function SentimentWidget({ score }: { score: SentimentScoreOut }) {
  const neutral = score.item_count - score.bullish_count - score.bearish_count;
  const total = score.item_count || 1;
  const bullishPct = Math.round((score.bullish_count / total) * 100);
  const bearishPct = Math.round((score.bearish_count / total) * 100);
  const neutralPct = 100 - bullishPct - bearishPct;

  const netScore = score.net_score ?? 0;
  const isPositive = netScore > 0.05;
  const isNegative = netScore < -0.05;

  const SentimentIcon = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;

  return (
    <div className="flex flex-col gap-3">
      {/* Net score */}
      <div className="flex items-center gap-3">
        <div
          className={clsx(
            "flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold",
            isPositive
              ? "bg-success/15 text-success"
              : isNegative
                ? "bg-danger/15 text-danger"
                : "bg-surface-subtle text-foreground-secondary",
          )}
        >
          <SentimentIcon size={22} />
        </div>
        <div>
          <div className="flex items-baseline gap-1.5">
            <span
              className={clsx(
                "text-2xl font-semibold tabular-nums",
                isPositive
                  ? "text-success"
                  : isNegative
                    ? "text-danger"
                    : "text-foreground-secondary",
              )}
            >
              {netScore >= 0 ? "+" : ""}
              {(netScore * 100).toFixed(0)}
            </span>
            <span className="text-xs text-foreground-muted">/ 100</span>
          </div>
          <p className="text-2xs text-foreground-muted">
            net sentiment · {score.item_count} item{score.item_count !== 1 ? "s" : ""} ·{" "}
            {platformLabel(score.platform)} · {fmtDate(score.as_of)}
          </p>
        </div>
      </div>

      {/* Bullish / Neutral / Bearish bar */}
      <div className="space-y-1">
        <div className="flex h-2 overflow-hidden rounded-full">
          {bullishPct > 0 && (
            <div
              className="bg-success"
              style={{ width: `${bullishPct}%` }}
              title={`Bullish ${bullishPct}%`}
            />
          )}
          {neutralPct > 0 && (
            <div
              className="bg-border-strong"
              style={{ width: `${neutralPct}%` }}
              title={`Neutral ${neutralPct}%`}
            />
          )}
          {bearishPct > 0 && (
            <div
              className="bg-danger"
              style={{ width: `${bearishPct}%` }}
              title={`Bearish ${bearishPct}%`}
            />
          )}
        </div>
        <div className="flex justify-between text-2xs text-foreground-muted">
          <span className="text-success">▲ {score.bullish_count} bullish</span>
          <span>{neutral} neutral</span>
          <span className="text-danger">{score.bearish_count} bearish ▼</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Post row
// ---------------------------------------------------------------------------

const SENTIMENT_CHIP: Record<string, { label: string; cls: string }> = {
  bullish: { label: "Bullish", cls: "text-success bg-success/10" },
  bearish: { label: "Bearish", cls: "text-danger bg-danger/10" },
  neutral: { label: "Neutral", cls: "text-foreground-muted bg-surface-subtle" },
};

function PostRow({ post }: { post: SocialPostOut }) {
  const chip = post.source_sentiment
    ? SENTIMENT_CHIP[post.source_sentiment.toLowerCase()]
    : undefined;

  const displayText = post.title
    ? `${post.title} — ${post.body}`
    : post.body;

  return (
    <div className="flex items-start gap-2 py-2">
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="rounded bg-surface-subtle px-1.5 py-0.5 text-2xs font-medium text-foreground-secondary">
            {platformLabel(post.platform)}
          </span>
          <span className="text-2xs text-foreground-muted">{post.author}</span>
          {chip && (
            <span className={clsx("rounded px-1.5 py-0.5 text-2xs font-medium", chip.cls)}>
              {chip.label}
            </span>
          )}
          {post.engagement_score != null && post.engagement_score > 0 && (
            <span className="text-2xs text-foreground-muted">
              ↑{post.engagement_score.toLocaleString()}
            </span>
          )}
          <span className="ml-auto text-2xs text-foreground-muted">
            {fmtDateTime(post.posted_at)}
          </span>
        </div>
        <p className="line-clamp-2 text-xs text-foreground-primary">
          {truncate(displayText, 200)}
        </p>
      </div>
      <a
        href={post.url}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-0.5 flex-shrink-0 text-foreground-muted hover:text-accent"
        title="Open source"
      >
        <ExternalLink size={12} />
      </a>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export function BehavioralPanel({ securityId }: Props) {
  const qc = useQueryClient();
  const [showAll, setShowAll] = useState(false);

  const sentiment = useQuery({
    queryKey: ["sentiment", securityId],
    queryFn: () => getSentiment(securityId),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const posts = useQuery({
    queryKey: ["social-posts", securityId],
    queryFn: () => listSocialPosts(securityId, { limit: 20 }),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const ingest = useMutation({
    mutationFn: () => ingestSocial(securityId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sentiment", securityId] });
      void qc.invalidateQueries({ queryKey: ["social-posts", securityId] });
    },
  });

  const noData =
    !sentiment.isLoading &&
    !posts.isLoading &&
    (sentiment.isError || !sentiment.data) &&
    (!posts.data || posts.data.length === 0);

  const action = (
    <Button
      size="sm"
      variant="ghost"
      loading={ingest.isPending}
      leadingIcon={<RefreshCw size={12} />}
      onClick={() => ingest.mutate()}
      title="Fetch latest social posts and recompute sentiment"
    >
      {ingest.isPending ? "Fetching…" : "Refresh"}
    </Button>
  );

  if (sentiment.isLoading || posts.isLoading) {
    return (
      <Panel title="Social sentiment" action={action}>
        <LoadingState />
      </Panel>
    );
  }

  if (noData) {
    return (
      <Panel title="Social sentiment" action={action}>
        <EmptyState
          title="No social data yet"
          description="Click Refresh to ingest the latest posts from StockTwits, Reddit and Hacker News."
        />
        {ingest.isError && (
          <p className="mt-2 text-xs text-danger">
            {(ingest.error as Error).message}
          </p>
        )}
      </Panel>
    );
  }

  const visiblePosts = showAll
    ? (posts.data ?? [])
    : (posts.data ?? []).slice(0, 5);

  return (
    <Panel
      title="Social sentiment"
      description="Aggregate signal from StockTwits, Reddit and Hacker News"
      action={action}
    >
      <div className="grid gap-4 lg:grid-cols-3">
        {/* Sentiment score — left column */}
        <div className="lg:border-r lg:border-border-subtle lg:pr-4">
          {sentiment.data ? (
            <SentimentWidget score={sentiment.data} />
          ) : (
            <p className="text-xs text-foreground-muted">No sentiment score yet.</p>
          )}
        </div>

        {/* Posts feed — right column */}
        <div className="space-y-1 lg:col-span-2">
          {visiblePosts.length === 0 ? (
            <p className="text-xs text-foreground-muted">No posts found for this security.</p>
          ) : (
            <div className="divide-y divide-border-subtle">
              {visiblePosts.map((p) => (
                <PostRow key={p.id} post={p} />
              ))}
            </div>
          )}

          {(posts.data?.length ?? 0) > 5 && (
            <button
              onClick={() => setShowAll((v) => !v)}
              className="mt-1 text-2xs text-accent hover:underline"
            >
              {showAll
                ? "Show fewer"
                : `Show all ${posts.data!.length} posts`}
            </button>
          )}
        </div>
      </div>

      {ingest.isSuccess && (
        <p className="mt-2 text-xs text-foreground-muted">
          Refreshed: {ingest.data.new_posts_inserted} new post
          {ingest.data.new_posts_inserted !== 1 ? "s" : ""} from{" "}
          {ingest.data.sources.join(", ") || "no sources"}.
        </p>
      )}
      {ingest.isError && (
        <p className="mt-2 text-xs text-danger">
          Refresh failed: {(ingest.error as Error).message}
        </p>
      )}
    </Panel>
  );
}
